import { useEffect, useState } from 'react';
import StatusCode from 'status-code-enum';

import {
  IGetAdsRequestFilter,
  IGetAdsRequestRange,
} from 'api/types/searchPageApiTypes';
import { savedSearchApi } from 'api/savedSearches';
import {
  mapFiltersRequest,
  mapRangesRequest,
} from 'components/SearchPage/mappers/filters';
import { mapMakeModelFilters } from 'components/SearchPage/hooks/useAds';
import { useSearchPageState } from 'components/SearchPage/context/SearchPageContext';
import { SORT_OPTIONS } from 'components/SearchPage/components/Sort/Sort';
import { formatPriceWithCurrency } from 'helpers/formatting';
import { formatSearchTerms } from 'helpers/formatSearchTerms';
import {
  useSearchFilterDispatch,
  useSearchFilterState,
} from 'components/SearchPage/context/SearchFilterContext';
import { MakeModelItem } from 'components/SearchPage/hooks/useFiltersMakeModel';
import { fireToast } from 'helpers/Toasts';
import { Link } from 'components/Toolkit/Button/Link';
import { useUserContext } from 'contexts/UserContext';
import { useOnUpdateOnly } from 'hooks/UseOnUpdateOnly';
import { USER_MENU } from 'components/Layouts/components/UserMenu';
import { useCreateSavedSearch } from 'components/SearchPage/hooks/useCreateSavedSearch';
import { useLocationContext } from 'features/location/Location.context';
import { formatAuthorizationHeader } from 'helpers/auth';

export enum NotificationFrequency {
  DAILY = 'DAILY',
  INSTANT = 'INSTANT',
  OFF = 'OFF',
}

function useSavedSearch(sectionName: string, accessToken: string | undefined) {
  const {
    user,
    isLoginModalOpen,
    setIsSaveSearchModalOpen,
    setIsLoginModalOpen,
    setIsInstantAlertAvailable,
  } = useUserContext();
  const [loginAndSaveSearch, setLoginAndSaveSearch] = useState(false);
  const { filters, ranges, makeModel } = useSearchFilterState();
  const { selectedCounty, countyTown, radius } = useLocationContext();

  const {
    baseUrl,
    section,
    sort,
    keywordSearchTerm,
    saved: searchPageSaved,
  } = useSearchPageState();
  const { getCountOfFiltersApplied, getFilter } = useSearchFilterDispatch();

  const {
    setSaved,
    createSavedSearch,
    createSavedSearchFromAd,
    saved,
    error,
    saving,
  } = useCreateSavedSearch({ saved: searchPageSaved, accessToken });

  function doorFormatter(filter: IGetAdsRequestFilter): IGetAdsRequestFilter {
    const values = filter.values.map((value) => `${value} doors`);
    return { ...filter, values };
  }

  function carFinanceFormatter(
    filter: IGetAdsRequestFilter,
  ): IGetAdsRequestFilter {
    if (filter.values.length && filter.values[0] === 'true') {
      return { ...filter, values: ['Price Per Month'] };
    }
    return filter;
  }

  function realisticPriceFormatter(
    filter: IGetAdsRequestFilter,
  ): IGetAdsRequestFilter {
    if (filter.values.length && filter.values[0] === 'true') {
      return { ...filter, values: ['realistic prices'] };
    }
    return filter;
  }

  function filterFormatter(filter: IGetAdsRequestFilter): IGetAdsRequestFilter {
    switch (filter.name) {
      case 'doors':
        return doorFormatter(filter);
      case 'carFinance':
        return carFinanceFormatter(filter);
      case 'containsValidPrice':
        return realisticPriceFormatter(filter);
      default:
        return filter;
    }
  }

  function addRangeDescriptor(range: IGetAdsRequestRange, descriptor: string) {
    return {
      ...range,
      from: range?.from
        ? range?.to && range.from !== range.to
          ? range.from
          : `${range.from}${descriptor}`
        : '',
      to: range?.to ? `${range.to}${descriptor}` : '',
    };
  }

  function priceFormatter(range: IGetAdsRequestRange) {
    const formatPriceValue = (value: string) => `
    ${formatPriceWithCurrency(parseInt(value), range.currency!)?.toString()!}
    `;
    return {
      ...range,
      to: range?.to ? formatPriceValue(range.to) : '',
      from: range?.from ? formatPriceValue(range.from) : '',
    };
  }

  function engineFormatter(range: IGetAdsRequestRange) {
    const formatEngine = (value?: string) =>
      value ? `${value.charAt(0)}.${value.charAt(1)}L` : '';
    return {
      ...range,
      to: range?.to ? formatEngine(range.to) : '',
      from: range?.from ? formatEngine(range.from) : '',
    };
  }

  function enginePowerFormatter(range: IGetAdsRequestRange) {
    return addRangeDescriptor(range, ' HP');
  }

  function batteryRangeFormatter(range: IGetAdsRequestRange) {
    return addRangeDescriptor(range, '+ km');
  }

  function seatsFormatter(range: IGetAdsRequestRange) {
    return addRangeDescriptor(range, ' Seats');
  }

  function mileageFormatter(range: IGetAdsRequestRange) {
    return addRangeDescriptor(range, ' km');
  }

  function rangeFormatter(range: IGetAdsRequestRange): IGetAdsRequestRange {
    switch (range.name) {
      case 'price':
        return priceFormatter(range);
      case 'engine':
        return engineFormatter(range);
      case 'enginePower':
        return enginePowerFormatter(range);
      case 'batteryRange':
        return batteryRangeFormatter(range);
      case 'seats':
        return seatsFormatter(range);
      case 'mileage':
        return mileageFormatter(range);
      default:
        return range;
    }
  }

  function generateSaveSearchTerms(): string {
    // [Section] · [Search Term] · [Filter 1] · [Filter 2] · (+6)
    const terms: string[] = [sectionName];

    const makeModelFilters = makeModel.filter((item) => item.makeValue !== '');

    // Make Models
    if (makeModelFilters && makeModelFilters.length > 0) {
      for (let makeModelItem of makeModel) {
        const { makeValue, modelValue } = makeModelItem;
        const hasMakeValue = makeValue && makeValue !== 'All Makes';
        if (hasMakeValue && modelValue.length === 0) {
          terms.push(`${makeValue} (All Models)`);
        } else if (hasMakeValue && modelValue.length === 1) {
          terms.push(`${makeValue} ${modelValue[0]}`);
        } else if (hasMakeValue && modelValue.length > 1) {
          terms.push(
            `${makeValue}, ${modelValue[0]} (+${modelValue.length - 1})`,
          );
        }
      }
    }

    if (keywordSearchTerm) {
      terms.push(`'${keywordSearchTerm}'`);
    }

    // Location
    if (selectedCounty?.displayName) {
      if (countyTown && radius) {
        terms.push(
          `${countyTown}, ${selectedCounty?.displayName} (+${radius}km)`,
        );
      } else terms.push(`${selectedCounty?.displayName} (All Areas)`);
    }

    // Filters
    for (const item of filters) {
      const filter = filterFormatter(item);
      const filterData = getFilter(item.name);
      if (filterData?.values) {
        for (const value of filter.values) {
          if (!value) break;
          const filterDataValue = filterData?.values.find(
            (item) => item.value === value,
          );
          terms.push(filterDataValue?.displayName || value);
        }
      }
    }

    // Ranges
    for (const item of ranges) {
      const range = rangeFormatter(item);

      if (range.from && range.to && range.from === range.to) {
        terms.push(`${range.from}`);
      } else {
        if (range.from && range.to) {
          terms.push(`${range.from} - ${range.to}`);
        } else if (range.from) {
          terms.push(`>${range.from}`);
        } else if (range.to) {
          terms.push(`<${range.to}`);
        }
      }
    }

    if (sort) {
      const sortDisplayName = SORT_OPTIONS.find((item) => item.value === sort);
      terms.push(`${sortDisplayName?.displayName}`);
    }

    return formatSearchTerms(terms);
  }

  function generateSavedSearchName() {
    let saveSearchName = '';

    const makeModelFilters = makeModel.filter(
      (item: MakeModelItem) => item.makeValue !== '',
    );

    if (makeModelFilters.length > 0) {
      if (makeModelFilters.length === 1) {
        const { makeValue, modelValue } = makeModelFilters[0];
        if (makeValue && makeValue !== 'All Makes' && modelValue.length === 1) {
          saveSearchName = `${makeValue} ${modelValue[0]}`;
        } else if (
          makeValue &&
          makeValue !== 'All Makes' &&
          (modelValue.length > 1 || modelValue.length === 0)
        ) {
          saveSearchName = `${makeValue}`;
        } else {
          saveSearchName = '';
        }
      } else {
        saveSearchName = `${makeModelFilters[0].makeValue} (+${
          makeModelFilters.length - 1
        }) `;
      }
    }

    return saveSearchName
      ? `${saveSearchName} in ${sectionName}`
      : `${keywordSearchTerm ? `${keywordSearchTerm} in ` : ''}${sectionName}`;
  }

  function checkIsSaveSearchDisabled() {
    if (saved) {
      return true;
    }
    return getCountOfFiltersApplied(keywordSearchTerm, sort) === 0;
  }

  async function handleOnClickSave() {
    if (user?.id) {
      try {
        const { data } = await savedSearchApi.searchPagePreCheck(
          user.id.toString(),
          {
            decayFunctions: [],
            makeModelFilters: mapMakeModelFilters(makeModel),
            filters: mapFiltersRequest(filters),
            ranges: mapRangesRequest(ranges),
            sort,
            terms: keywordSearchTerm,
            sections: [section],
            scoringRanges: [],
            scoringFilters: [],
            notFilters: [],
            paging: { pageSize: 30, from: 0 },
          },
          formatAuthorizationHeader(accessToken),
        );
        if (data.instantAlert) {
          setIsInstantAlertAvailable(true);
        } else {
          setIsInstantAlertAvailable(false);
        }
        setIsSaveSearchModalOpen(true);
      } catch (error) {
        if (
          error?.response?.status === StatusCode.ClientErrorUnprocessableEntity
        ) {
          fireToast({
            type: 'ERROR',
            text: (
              <>
                You already have a Saved Search for these filters.{' '}
                <Link
                  ofType="SECONDARY"
                  inline
                  href={`${baseUrl}/dashboard/searches`}
                >
                  View all Saved Searches
                </Link>
              </>
            ),
            closeOnClick: false,
          });
        } else {
          fireToast({
            type: 'ERROR',
            text: 'Oops! Something went wrong, please try again later',
          });
        }
      }
    } else {
      // Set intent to save search after login and open login modal
      setLoginAndSaveSearch(true);
      setIsLoginModalOpen(true);
    }
  }

  useEffect(() => {
    if (loginAndSaveSearch && !isLoginModalOpen) {
      if (user) {
        // User has logged in so remove intent and open save search modal
        setIsSaveSearchModalOpen(true);
        setLoginAndSaveSearch(false);
      } else {
        // The user cancelled the login so intent to save search is removed
        setLoginAndSaveSearch(false);
      }
    }
  }, [isLoginModalOpen]);

  useOnUpdateOnly(() => {
    if (saved) {
      setIsSaveSearchModalOpen(false);
      fireToast({
        type: 'SUCCESS',
        text: (
          <>
            Search Saved! Remember you can manage your preferences in{' '}
            <Link
              href={`${baseUrl}/dashboard/${USER_MENU.SEARCHES}`}
              ofType="SECONDARY"
              inline
            >
              My DoneDeal
            </Link>
          </>
        ),
      });
    }
  }, [saved]);

  useOnUpdateOnly(() => {
    if (error) {
      fireToast({
        type: 'ERROR',
        text: error,
      });
    }
  }, [error]);

  useOnUpdateOnly(() => {
    if (saved) {
      setSaved(false);
    }
  }, [filters, ranges, sort, keywordSearchTerm, makeModel, selectedCounty]);

  return {
    handleOnClickSave,
    savedSearchTerms: generateSaveSearchTerms(),
    isSavedSearchDisabled: checkIsSaveSearchDisabled(),
    savedSearchName: generateSavedSearchName(),
    createSavedSearch,
    createSavedSearchFromAd,
    saved,
    saving,
  };
}

export { useSavedSearch };
